/**
 * 题目API函数
 * 提供获取题目列表、题目详情、题型相关操作等功能
 */
import { supabase } from '../supabaseClient';
import { mapTypeIdToName, getTypeIdByCode } from './questionTypesMapAPI';

/**
 * 获取题目列表
 * @param {object} options - 查询选项
 * @returns {Promise<Array>} 题目列表
 */
export const getQuestions = async (options = {}) => {
  try {
    const { 
      limit, 
      subjectId, 
      type, 
      orderBy = '_id', 
      ascending = false,
      includeAnswers = false,
      examId
    } = options;
    
    let query = supabase
      .from('questions')
      .select('*')
      .order(orderBy, { ascending });
      
    // 添加筛选条件
    if (subjectId) {
      query = query.eq('subject_id', Number(subjectId));
    }
    
    if (examId) {
      query = query.eq('examid', Number(examId));
    }
    
    if (type) {
      query = query.eq('type', type);
    }
    
    if (limit) {
      query = query.limit(limit);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    // 移除答案信息（如果不需要）
    if (!includeAnswers && data) {
      return data.map(q => {
        const { answer, ...rest } = q;
        return rest;
      });
    }
    
    return data || [];
  } catch (error) {
    console.error('获取题目列表出错:', error);
    throw error;
  }
};

/**
 * 获取题目详情
 * @param {number} questionId - 题目ID
 * @param {boolean} includeAnswers - 是否包含答案
 * @returns {Promise<Object>} 题目详情
 */
export const getQuestionById = async (questionId, includeAnswers = false) => {
  try {
    const { data, error } = await supabase
      .from('questions')
      .select('*')
      .eq('_id', Number(questionId))
      .single();
      
    if (error) throw error;
    
    // 移除答案信息（如果不需要）
    if (!includeAnswers && data) {
      const { answer, ...rest } = data;
      return rest;
    }
    
    return data;
  } catch (error) {
    console.error('获取题目详情出错:', error);
    throw error;
  }
};

/**
 * 获取科目下所有题目
 * @param {number} subjectId - 科目ID
 * @param {boolean} includeAnswers - 是否包含答案
 * @returns {Promise<Array>} 题目列表
 */
export const getQuestionsBySubject = async (subjectId, includeAnswers = false) => {
  return getQuestions({ subjectId, includeAnswers });
};

/**
 * 随机获取指定数量的题目
 * @param {object} options - 查询选项
 * @returns {Promise<Array>} 随机题目列表
 */
export const getRandomQuestions = async (options = {}) => {
  try {
    const { subjectId, type, count = 10, includeAnswers = false } = options;
    
    let query = supabase
      .from('questions')
      .select('*');
      
    // 添加筛选条件
    if (subjectId) {
      query = query.eq('subject_id', Number(subjectId));
    }
    
    if (type) {
      query = query.eq('type', type);
    }
    
    // 获取所有符合条件的题目
    const { data, error } = await query;
    
    if (error) throw error;
    
    if (!data || data.length === 0) {
      return [];
    }
    
    // 随机打乱并获取指定数量的题目
    const shuffled = [...data].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, count);
    
    // 移除答案信息（如果不需要）
    if (!includeAnswers) {
      return selected.map(q => {
        const { answer, ...rest } = q;
        return rest;
      });
    }
    
    return selected;
  } catch (error) {
    console.error('获取随机题目出错:', error);
    throw error;
  }
};

/**
 * 创建新题目
 * @param {object} questionData - 题目数据
 * @returns {Promise<object>} 创建的题目数据
 */
export const createQuestion = async (questionData) => {
  try {
    const { data, error } = await supabase
      .from('questions')
      .insert([questionData])
      .select();
      
    if (error) throw error;
    
    return data[0];
  } catch (error) {
    console.error('创建题目出错:', error);
    throw error;
  }
};

/**
 * 更新题目信息
 * @param {number} questionId - 题目ID
 * @param {object} updates - 更新的数据
 * @returns {Promise<object>} 更新后的题目数据
 */
export const updateQuestion = async (questionId, updates) => {
  try {
    const { data, error } = await supabase
      .from('questions')
      .update(updates)
      .eq('_id', Number(questionId))
      .select();
      
    if (error) throw error;
    
    return data[0];
  } catch (error) {
    console.error('更新题目出错:', error);
    throw error;
  }
};

/**
 * 删除题目
 * @param {number} questionId - 题目ID
 * @returns {Promise<void>}
 */
export const deleteQuestion = async (questionId) => {
  try {
    const { error } = await supabase
      .from('questions')
      .delete()
      .eq('_id', Number(questionId));
      
    if (error) throw error;
  } catch (error) {
    console.error('删除题目出错:', error);
    throw error;
  }
};

/**
 * 检查答案是否正确
 * @param {number} questionId - 题目ID
 * @param {string|Array} userAnswer - 用户答案，可以是字符串或数组
 * @returns {Promise<boolean>} 是否正确
 */
export const checkAnswer = async (questionId, userAnswer) => {
  try {
    const question = await getQuestionById(questionId, true);
    
    if (!question) {
      throw new Error('题目不存在');
    }
    
    const correctAnswer = question.answer;
    
    // 处理不同题型的答案比较
    if (question.type === '3' || question.type === 'multi' || question.type === '多选') {
      // 多选题：答案是数组
      const userAnswerArray = Array.isArray(userAnswer) ? userAnswer.sort() : [userAnswer].sort();
      const correctAnswerArray = Array.isArray(correctAnswer) ? correctAnswer.sort() : 
                               (typeof correctAnswer === 'string' ? correctAnswer.split(',').sort() : [correctAnswer].sort());
      
      return JSON.stringify(userAnswerArray) === JSON.stringify(correctAnswerArray);
    } else {
      // 单选题和判断题：直接比较
      return userAnswer.toString() === correctAnswer.toString();
    }
  } catch (error) {
    console.error('检查答案出错:', error);
    throw error;
  }
};

/**
 * 获取题型名称
 * @param {string|number} type - 题型ID或代码
 * @returns {string} 题型名称
 */
export const getTypeName = (type) => {
  // 使用 questionTypesMapAPI 中的函数
  return mapTypeIdToName(getTypeIdByCode(type));
};

/**
 * 根据考试ID获取题目
 * @param {number} examId - 考试ID
 * @param {boolean} includeAnswers - 是否包含答案
 * @returns {Promise<Array>} 题目列表
 */
export const getQuestionsByExamId = async (examId, includeAnswers = false) => {
  return getQuestions({ examId, includeAnswers });
}; 